import React from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TableHead,
  IconButton,
  Fade,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import { Howl } from "howler";

const buttonClickSound = new Howl({
  src: ["/assets/audio/click-button.ogg", "/assets/audio/click-button.mp3"],
  volume: 0.2,
});

// Match the game's tile colors
const TILE_COLORS = {
  2: "#abc9eb",
  4: "#89aed8",
  8: "#6999cf",
  16: "#4c83c2",
  32: "#4ca6c2",
  64: "#59b8a7",
  128: "#82b879",
  256: "#b6bf73",
  512: "#baa5d9",
  1024: "#e0a580",
};

const mergeExamples = [
  {
    merge: "2 + 2",
    mergeColor: TILE_COLORS[2],
    newTile: 4,
    newTileColor: TILE_COLORS[4],
    points: 4,
  },
  {
    merge: "8 + 8",
    mergeColor: TILE_COLORS[8],
    newTile: 16,
    newTileColor: TILE_COLORS[16],
    points: 16,
  },
  {
    merge: "128 + 128",
    mergeColor: TILE_COLORS[128],
    newTile: 256,
    newTileColor: TILE_COLORS[256],
    points: 256,
  },
];

const ScoreInfoModal = ({ open, onClose }) => (
  <Dialog
    open={open}
    onClose={() => {
      buttonClickSound.play();
      onClose();
    }}
    maxWidth="xs"
    fullWidth
    PaperProps={{
      sx: {
        borderRadius: 2,
        boxShadow: "0 4px 20px rgba(0,0,0,0.18)",
        border: "2px solid #7fd6f7",
        p: 0,
      },
    }}
    BackdropProps={{ style: { backgroundColor: "rgba(0,0,0,0.18)" } }}
    transitionDuration={300}
  >
    <Box sx={{ position: "absolute", top: 16, right: 16, zIndex: 10 }}>
      <IconButton
        onClick={() => {
          buttonClickSound.play();
          onClose();
        }}
        aria-label="Close"
        size="large"
        sx={{
          minWidth: 44,
          minHeight: 44,
          p: 1,
          "&:hover": {
            backgroundColor: "rgba(0,0,0,0.04)",
          },
        }}
        title="Close"
      >
        <CloseIcon sx={{ fontSize: 28 }} />
      </IconButton>
    </Box>
    <DialogContent
      sx={{
        p: { xs: 2, sm: 3 },
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        minWidth: { xs: 280, sm: 320 },
        minHeight: 140,
      }}
    >
      <Fade in={true} timeout={350}>
        <Box
          sx={{
            width: "100%",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 1.5,
          }}
        >
          {/* Contextual Hook */}
          <Typography
            variant="body2"
            sx={{
              color: "#7fd6f7",
              fontWeight: 600,
              fontSize: { xs: "0.875rem", sm: "1rem" },
              mb: 0.5,
            }}
          >
            New Scoring System
          </Typography>

          <EmojiEventsIcon
            sx={{ fontSize: 36, color: "#FFD700", mb: 0.5 }}
            aria-label="Scoring"
          />
          <Box sx={{ textAlign: "center", mb: 1 }}>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 600,
                color: "#333",
                fontSize: { xs: "1.25rem", sm: "1.4rem" },
                mb: 0.5,
              }}
            >
              How Scoring Works
            </Typography>
          </Box>
          <Typography
            variant="body1"
            sx={{
              textAlign: "center",
              mb: 1.5,
              fontSize: { xs: "1rem", sm: "1.1rem" },
              color: "#444",
              lineHeight: 1.5,
            }}
          >
            Merging tiles now gives you points.
            <br />
            Every time two tiles combine, you earn the number on the new tile.
          </Typography>
          <Typography
            variant="body1"
            sx={{
              textAlign: "center",
              mb: 2,
              fontSize: { xs: "1rem", sm: "1.1rem" },
              color: "#444",
              lineHeight: 1.5,
              fontStyle: "italic",
            }}
          >
            It’s a simple way to feel a little spark of progress — one merge at
            a time.
          </Typography>

          {/* Subtle separator line */}
          <Box
            sx={{
              width: "60%",
              height: 1,
              backgroundColor: "#E0E0E0",
              mb: 3,
            }}
          />

          <Box sx={{ width: "100%", mb: 2 }}>
            <Table
              size="small"
              sx={{
                "& .MuiTableCell-root": {
                  borderColor: "#E0E0E0",
                  fontSize: { xs: "0.875rem", sm: "1rem" },
                },
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      fontWeight: 700,
                      color: "#222",
                      fontSize: { xs: "0.875rem", sm: "1rem" },
                      minWidth: 140,
                      whiteSpace: "nowrap",
                    }}
                  >
                    Merge
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 700,
                      color: "#222",
                      fontSize: { xs: "0.875rem", sm: "1rem" },
                    }}
                  >
                    New Tile
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 700,
                      color: "#222",
                      fontSize: { xs: "0.875rem", sm: "1rem" },
                    }}
                  >
                    Points Earned
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {mergeExamples.map((ex, i) => (
                  <TableRow key={i}>
                    <TableCell
                      sx={{
                        color: "#666",
                        minWidth: 140,
                        whiteSpace: "nowrap",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                          whiteSpace: "nowrap",
                        }}
                      >
                        <Box
                          sx={{
                            width: 16,
                            height: 16,
                            borderRadius: 1,
                            backgroundColor: ex.mergeColor,
                            border: "1px solid rgba(255,255,255,0.8)",
                            boxShadow:
                              "inset 0 1px 2px rgba(0,0,0,0.1), 0 1px 1px rgba(255,255,255,0.3)",
                          }}
                        />
                        {ex.merge}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ color: "#666" }}>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Box
                          sx={{
                            width: 16,
                            height: 16,
                            borderRadius: 1,
                            backgroundColor: ex.newTileColor,
                            border: "1px solid rgba(255,255,255,0.8)",
                            boxShadow:
                              "inset 0 1px 2px rgba(0,0,0,0.1), 0 1px 1px rgba(255,255,255,0.3)",
                          }}
                        />
                        {ex.newTile}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ color: "#666", fontWeight: 500 }}>
                      {ex.points}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        </Box>
      </Fade>
    </DialogContent>
    <DialogActions
      sx={{
        p: 3,
        pt: 0,
        justifyContent: "center",
      }}
    >
      <Button
        onClick={() => {
          buttonClickSound.play();
          onClose();
        }}
        variant="contained"
        sx={{
          borderRadius: 2,
          px: 3,
          py: 1,
          bgcolor: "#7fd6f7",
          color: "#222",
          "&:hover": {
            bgcolor: "#5780bd",
          },
        }}
      >
        Let's play!
      </Button>
    </DialogActions>
  </Dialog>
);

export default ScoreInfoModal;
